import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { SortHelper } from '../../SortHelper';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { Customer } from '../../interfaces/Customer';
import { Consultant } from '../../interfaces/Consultant';
import { dataActionCreators, Project, ProjectAssigment } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { useTranslation } from 'react-i18next';
import { Button, Option, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, Switch, SwitchOnChangeData, Textarea, Dropdown, Field, useToastController, Toast, ToastTitle, ToastTrigger, Link, InputOnChangeData, TextareaOnChangeData } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { onFormatDate } from '../Tools/converters';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { ConsultantDropdown } from '../EditorHelpers/ConsultantDropdown';
import { ProjectDropdown } from '../EditorHelpers/ProjectDropdown';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { FileViewer } from '../FileViewer/FileViewer';
import { DownloadFile } from '../Tools/FileDownloadHelper';
import { VATDropdown } from '../EditorHelpers/VATDropdown';
import { useConsultants, useCustomers, useProjects } from '../Tools/ObjectLoader';

export const ProjectAssigmentEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const projectAssigmentEdit = useSelector<ApplicationState, ProjectAssigment | null>((state: ApplicationState) => state.data.projectAssigmentEdit);
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { projects } = useProjects();
    const { consultants } = useConsultants();
    const { customers } = useCustomers();
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [displayName, setDisplayName] = React.useState(projectAssigmentEdit ? projectAssigmentEdit.displayName : "");
    const [project, setProject] = React.useState<Project | null | undefined>(projects ? projects.find((c) => projectAssigmentEdit && c.projectid === projectAssigmentEdit.projectid) : undefined);
    const [customer, setCustomer] = React.useState<Customer | null | undefined>(customers && project && projects ? customers.find((c) => project && c.customerid === project.customerid) : undefined);
    const [customerProjects, setCustomerProjects] = React.useState<Project[]>(projects ? projects.filter((c) => projectAssigmentEdit && customer && c.customerid === customer.customerid) : []);
    const [consultant, setConsultant] = React.useState<Consultant | null | undefined>(consultants ? consultants.find((c) => projectAssigmentEdit && c.consultantid === projectAssigmentEdit.consultantid) : undefined);
    const [customerSignedTimeSheetNeeded, setCustomerSignedTimeSheetNeeded] = React.useState<boolean>(projectAssigmentEdit ? projectAssigmentEdit.customerSignedTimeSheetNeeded : true);
    const [consultantPrice, setConsultantPrice] = React.useState<number>(projectAssigmentEdit && projectAssigmentEdit.consultantPrice ? projectAssigmentEdit.consultantPrice : 0);
    const [consultantVatcode, setConsultantVatcode] = React.useState<number>(projectAssigmentEdit ? projectAssigmentEdit.consultantVatcode : 21);
    const [customerPrice, setCustomerPrice] = React.useState<number>(projectAssigmentEdit && projectAssigmentEdit.customerPrice ? projectAssigmentEdit.customerPrice : 0);
    const [customerVatcode, setCustomerVatcode] = React.useState<number>(projectAssigmentEdit ? projectAssigmentEdit.customerVatcode : 21);
    const [startDate, setStartDate] = React.useState<Date | null>(projectAssigmentEdit && projectAssigmentEdit.dateDebut ? new Date(projectAssigmentEdit.dateDebut) : null);
    const [endDate, setEndDate] = React.useState<Date | null>(projectAssigmentEdit && projectAssigmentEdit.dateFin ? new Date(projectAssigmentEdit.dateFin) : null);
    const [po, setPo] = React.useState(projectAssigmentEdit ? projectAssigmentEdit.po : "");
    const [poQuantity, setQuantity] = React.useState(projectAssigmentEdit ? projectAssigmentEdit.poquantity : null);
    const [unitOfTime, setUnitOfTime] = React.useState<string>(projectAssigmentEdit && projectAssigmentEdit.unitOfTime ? projectAssigmentEdit.unitOfTime : "H");
    const [description, setDescription] = React.useState(projectAssigmentEdit ? projectAssigmentEdit.description : "");
    const [consultantCode, setConsultantCode] = React.useState(projectAssigmentEdit ? projectAssigmentEdit.consultantCode : null);
    const [buyerItemIdentification, setBuyerItemIdentification] = React.useState<string | null>(projectAssigmentEdit && projectAssigmentEdit.buyerItemIdentification);
    const [sellerItemIdentification, setSellerItemIdentification] = React.useState<string | null>(projectAssigmentEdit && projectAssigmentEdit.sellerItemIdentification);
    const [peppolAttachmentInvoiceName, setPeppolAttachmentInvoiceName] = React.useState<string | null>(projectAssigmentEdit && projectAssigmentEdit.peppolAttachmentInvoiceName);
    const [peppolAttachmentTsname, setPeppolAttachmentTsname] = React.useState<string | null>(projectAssigmentEdit && projectAssigmentEdit.peppolAttachmentTsname);
    const [startDateTS, setStartDateTS] = React.useState<Date | null>(projectAssigmentEdit && projectAssigmentEdit.dateDebutTsencoding ? new Date(projectAssigmentEdit.dateDebutTsencoding) : null);
    const [endDateTS, setEndDateTS] = React.useState<Date | null>(projectAssigmentEdit && projectAssigmentEdit.dateFinTsencoding ? new Date(projectAssigmentEdit.dateFinTsencoding) : null);

    // Fields validation
    const [isDisplayNameValid, setDisplayNameValid] = React.useState(false);
    const [isProjectValid, setProjectValid] = React.useState(false);
    const [isConsultantValid, setConsultantValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    const isEditAndFile = React.useMemo(() => { return (projectAssigmentEdit && projectAssigmentEdit.orderSharePointFileUniqueId) ?? false; }, [projectAssigmentEdit]);
    const [file, setFile] = React.useState<Blob>();

    React.useEffect(() => {
        if (isEditAndFile)
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null && projectAssigmentEdit && projectAssigmentEdit.projectAssignmentid) {
                        DownloadFile('api/ProjectsAssigments/' + projectAssigmentEdit.projectAssignmentid + "/DownloadProjectAssigmentOrderFile", token)
                            .then((f) => {
                                if (f) {
                                    setFile(f);
                                }
                            })
                            .catch((e) => { console.log(e); });
                    }
                });
    }, [isEditAndFile]);

    React.useEffect(() => {
        const p = projects
            ? projects
                .filter((c) => projectAssigmentEdit && customer && c.customerid === customer.customerid && (c.dateFin === null || (new Date(c.dateFin).getTime() >= Date.now())))
                .sort(SortHelper.ProjectByName)
            : [];
        setCustomerProjects(p);
    }, [customer]);
    React.useEffect(() => {
        setDisplayNameValid(displayName !== undefined && displayName !== null && displayName.length > 0);
    }, [displayName]);
    React.useEffect(() => {
        setProjectValid(project !== null && project !== undefined);
    }, [project]);
    React.useEffect(() => {
        setConsultantValid(consultant !== null && consultant !== undefined);
    }, [consultant]);
    React.useEffect(() => {
        let submitValid = isDisplayNameValid && isProjectValid && isConsultantValid;
        submitValid = submitValid && startDate !== null && (endDate === null || startDate.getTime() <= endDate.getTime());
        submitValid = submitValid && startDateTS !== null && endDateTS !== null && startDateTS.getTime() <= endDateTS.getTime();
        if (submitValid && projectAssigmentEdit) {
            // If valid, check if any change on existing item
            submitValid = submitValid && (
                displayName !== projectAssigmentEdit.displayName
                || (project !== null && project !== undefined && project.projectid !== projectAssigmentEdit.projectid)
                || (consultant !== null && consultant !== undefined && consultant.consultantid !== projectAssigmentEdit.consultantid)
                || (consultantPrice !== projectAssigmentEdit.consultantPrice)
                || (consultantVatcode !== projectAssigmentEdit.consultantVatcode)
                || (customerPrice !== projectAssigmentEdit.customerPrice)
                || (customerVatcode !== projectAssigmentEdit.customerVatcode)
                || (unitOfTime !== projectAssigmentEdit.unitOfTime)
                || startDate !== (projectAssigmentEdit.dateDebut ? new Date(projectAssigmentEdit.dateDebut) : null)
                || endDate !== (projectAssigmentEdit.dateFin ? new Date(projectAssigmentEdit.dateFin) : null)
                || description !== projectAssigmentEdit.description
                || poQuantity !== projectAssigmentEdit.poquantity
                || consultantCode !== projectAssigmentEdit.consultantCode
                || po !== projectAssigmentEdit.po
                || poQuantity !== projectAssigmentEdit.poquantity
                || startDateTS !== (projectAssigmentEdit.dateDebutTsencoding ? new Date(projectAssigmentEdit.dateDebutTsencoding) : null)
                || endDateTS !== (projectAssigmentEdit.dateFinTsencoding ? new Date(projectAssigmentEdit.dateFinTsencoding) : null)
                || customerSignedTimeSheetNeeded !== projectAssigmentEdit.customerSignedTimeSheetNeeded
                || buyerItemIdentification !== projectAssigmentEdit.buyerItemIdentification
                || sellerItemIdentification !== projectAssigmentEdit.sellerItemIdentification
                || peppolAttachmentInvoiceName !== projectAssigmentEdit.peppolAttachmentInvoiceName
                || peppolAttachmentTsname !== projectAssigmentEdit.peppolAttachmentTsname
            );
        }
        setSubmitValid(submitValid);
    }, [displayName, isDisplayNameValid,
        startDate, endDate,
        project, isProjectValid,
        consultant, isConsultantValid,
        consultantPrice, consultantVatcode, customerPrice, customerVatcode, unitOfTime, description, poQuantity, consultantCode, po, startDateTS, endDateTS, customerSignedTimeSheetNeeded, buyerItemIdentification, sellerItemIdentification, peppolAttachmentInvoiceName, peppolAttachmentTsname
    ]);

    console.log("Render ProjectAssigmentEditor", { displayName, project, consultant, startDate, endDate, consultantPrice, consultantVatcode, customerPrice, customerVatcode, unitOfTime, description, poQuantity, consultantCode, po, startDateTS, endDateTS, customerSignedTimeSheetNeeded, buyerItemIdentification, sellerItemIdentification, peppolAttachmentInvoiceName, peppolAttachmentTsname });

    const submit = () => {
        const c: ProjectAssigment = {
            projectAssignmentid: projectAssigmentEdit ? projectAssigmentEdit.projectAssignmentid : null,
            projectid: project ? project.projectid : null,
            consultantid: consultant ? consultant.consultantid : null,
            consultantCode: consultantCode ? consultantCode : null,
            consultantPrice: consultantPrice,
            consultantVatcode: consultantVatcode,
            customerPrice: customerPrice,
            customerVatcode: customerVatcode,
            displayName: displayName ? displayName : "",
            dateDebut: ConvertionHelpers.DateToISODateOnly(startDate),
            dateFin: ConvertionHelpers.DateToISODateOnly(endDate),
            customerSignedTimeSheetNeeded: customerSignedTimeSheetNeeded,
            po: po,
            description: description,
            poquantity: poQuantity === 0 ? null : poQuantity,
            unitOfTime: unitOfTime,
            orderSharePointFileUniqueId: projectAssigmentEdit ? projectAssigmentEdit.orderSharePointFileUniqueId : null,
            buyerItemIdentification: buyerItemIdentification,
            sellerItemIdentification: sellerItemIdentification,
            peppolAttachmentInvoiceName: peppolAttachmentInvoiceName,
            peppolAttachmentTsname: peppolAttachmentTsname,
            dateDebutTsencoding: ConvertionHelpers.DateToISODateOnly(startDateTS),
            dateFinTsencoding: ConvertionHelpers.DateToISODateOnly(endDateTS),
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                Saving assigment ...
                            </ToastTitle>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/ProjectsAssigments/" + (projectAssigmentEdit && projectAssigmentEdit.projectAssignmentid !== null ? projectAssigmentEdit.projectAssignmentid : ""), {
                        method: projectAssigmentEdit && projectAssigmentEdit.projectAssignmentid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Assigment saved
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<ProjectAssigment>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Assigment save failed
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) {
                                dispatch(dataActionCreators.patchProjectAssigmentEdition(data));
                                dispatch(dataActionCreators.requestProjectAssigmentEdition(null));
                            }
                        });
                }
            });
    };

    const PriceTypeSelection = <>
        <Option key="H" value="H">Hours</Option>
        <Option key="D" value="D">Days</Option>
        <Option key="M" value="M">Month</Option>
        <Option key="F" value="F">Fixed</Option>
    </>;
    const getPriceTypeSelectionString = (s: string): string => {
        switch (s) {
            case "H": return t('Hours');
            case "D": return t('Days');
            case "M": return t('Month');
            case "F": return t('Fixed');
        }
        return "Unknown";
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={isEditAndFile ? styles.DialogContentWithFilePreview : undefined}>
            <DialogBody className={isEditAndFile ? styles.fluid : undefined}>
                <DialogTitle>{projectAssigmentEdit && projectAssigmentEdit.projectAssignmentid !== null ? "Edit project assigment " + projectAssigmentEdit.displayName : "Create new project assigment"}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        {isEditAndFile &&
                            <FileViewer file={file} />
                        }
                        <div className={isEditAndFile ? styles.InfoPart : styles.fluid}>
                            <form
                                onSubmit={() => {
                                    dispatch(dataActionCreators.requestProjectAssigmentEdition(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                <div className={styles.ContentBoxBrandBackground}>
                                    <Field label={t('Customer')} className={styles.fluid} required>
                                        <CustomerDropdown currentCustomer={customer ?? undefined}
                                            customers={customers ?? []}
                                            setCustomer={(c?: Customer) => { setCustomer(c); setProject(undefined); }} />
                                    </Field>
                                    <Field label={t('Project')} required>
                                        <ProjectDropdown currentProject={project ?? undefined}
                                            setProject={setProject}
                                            projects={customerProjects}
                                            ariaInvalid={project === null || project === undefined}
                                        />
                                    </Field>
                                    <Field label={t('DisplayName')} required>
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setDisplayName(data.value)}
                                            value={displayName !== null ? displayName : ""}
                                            maxLength={100}
                                        />
                                        {/*    error={!isDisplayNameValid}*/}
                                    </Field>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field40}>
                                            <Field label="PO" className={styles.fluid}>
                                                <Input
                                                    autoComplete="off"
                                                    className={styles.fluid}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setPo(data.value)}
                                                    value={po === null ? "" : po}
                                                    maxLength={100}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field30}>
                                            <Field label="Quantity" className={styles.fluid}>
                                                <CurrencyInput
                                                    setCurrency={setQuantity}
                                                    currency={poQuantity}
                                                    digits={3}
                                                    contentAfter={getPriceTypeSelectionString(unitOfTime)}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field30}>
                                            <Field label={t('PriceUnits')} className={styles.fluid}>
                                                <Dropdown
                                                    className={styles.fluid}
                                                    onOptionSelect={(event, data) => {
                                                        setUnitOfTime(data.optionValue ? data.optionValue : "H");
                                                    }}
                                                    value={getPriceTypeSelectionString(unitOfTime)}
                                                    selectedOptions={[unitOfTime]}
                                                >
                                                    {PriceTypeSelection}
                                                </Dropdown>
                                            </Field>
                                        </div>
                                    </div>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label={t('Start')} className={styles.fluid} required>
                                                <DatePicker className={styles.fluid}
                                                    onSelectDate={(date: Date | null | undefined) => { setStartDate(date === undefined ? null : date); }}
                                                    value={startDate === null ? undefined : startDate}
                                                    formatDate={onFormatDate}
                                                    placeholder={t('SelectStartDate')}
                                                    maxDate={endDate === null ? undefined : endDate}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label={t('End')} className={styles.fluid}>
                                                <DatePicker className={styles.fluid}
                                                    onSelectDate={(date: Date | null | undefined) => { setEndDate(date === undefined ? null : date); }}
                                                    value={endDate === null ? undefined : endDate}
                                                    formatDate={onFormatDate}
                                                    placeholder={t('SelectEndDate')}
                                                    minDate={startDate === null ? undefined : startDate}
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.ContentBoxBrandBackground}>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label={t('Consultant')} className={styles.fluid} required>
                                                <ConsultantDropdown
                                                    currentConsultant={consultant ?? undefined}
                                                    setConsultant={setConsultant}
                                                    ariaInvalid={!consultant}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label={t('Consultant') + " (code)"} className={styles.fluid}>
                                                <Input
                                                    autoComplete="off"
                                                    className={styles.fluid}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setConsultantCode(data.value)}
                                                    value={consultantCode !== null ? consultantCode : ""}
                                                    maxLength={100}
                                                />
                                                {/*    error={!isDisplayNameValid}*/}
                                            </Field>
                                        </div>
                                    </div>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field70}>
                                            <Field label={t('CustomerPrice')} className={styles.fluid}>
                                                <CurrencyInput
                                                    setCurrency={setCustomerPrice}
                                                    currency={customerPrice}
                                                    digits={3}
                                                    contentAfter={"€/" + getPriceTypeSelectionString(unitOfTime)}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field30}>
                                            <Field label={t('VAT')} className={styles.fluid}>
                                                <VATDropdown currentVAT={customerVatcode} setVatCode={setCustomerVatcode} />
                                            </Field>
                                        </div>
                                    </div>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field70}>
                                            <Field label={t('ConsultantPrice')} className={styles.fluid}>
                                                <CurrencyInput
                                                    setCurrency={setConsultantPrice}
                                                    currency={consultantPrice}
                                                    digits={3}
                                                    contentAfter={"€/" + getPriceTypeSelectionString(unitOfTime)}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field30}>
                                            <Field label={t('VAT')} className={styles.fluid}>
                                                <VATDropdown currentVAT={consultantVatcode} setVatCode={setConsultantVatcode} />
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.ContentBoxBrandBackground}>
                                    <Field label="Description">
                                        <Textarea
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setDescription(data.value)}
                                            value={description !== null ? description : ""}
                                            resize="vertical"
                                        />
                                    </Field>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label="Peppol Buyer Item Identification" className={styles.fluid}>
                                                <Input
                                                    autoComplete="off"
                                                    className={styles.fluid}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setBuyerItemIdentification(data.value)}
                                                    value={buyerItemIdentification !== null ? buyerItemIdentification : ""}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label="Peppol Seller Item Identification" className={styles.fluid}>
                                                <Input
                                                    autoComplete="off"
                                                    className={styles.fluid}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setSellerItemIdentification(data.value)}
                                                    value={sellerItemIdentification !== null ? sellerItemIdentification : ""}
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label="Peppol Attachment Invoice Name" className={styles.fluid}>
                                                <Input
                                                    autoComplete="off"
                                                    className={styles.fluid}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setPeppolAttachmentInvoiceName(data.value)}
                                                    value={peppolAttachmentInvoiceName !== null ? peppolAttachmentInvoiceName : ""}
                                                    maxLength={100}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label="Peppol Attachment TimeSheet Name" className={styles.fluid}>
                                                <Input
                                                    autoComplete="off"
                                                    className={styles.fluid}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setPeppolAttachmentTsname(data.value)}
                                                    value={peppolAttachmentTsname !== null ? peppolAttachmentTsname : ""}
                                                    maxLength={100}
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                    <Switch checked={customerSignedTimeSheetNeeded} label="Need customer signed TimeSheet" onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setCustomerSignedTimeSheetNeeded(data.checked); }} />
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label={t('Start')} className={styles.fluid} required>
                                                <DatePicker className={styles.fluid}
                                                    onSelectDate={(date: Date | null | undefined) => { setStartDateTS(date === undefined ? null : date); }}
                                                    value={startDateTS === null ? undefined : startDateTS}
                                                    formatDate={onFormatDate}
                                                    placeholder={t('SelectStartDate')}
                                                    maxDate={endDateTS === null ? undefined : endDateTS}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label={t('End')} className={styles.fluid} required>
                                                <DatePicker className={styles.fluid}
                                                    onSelectDate={(date: Date | null | undefined) => { setEndDateTS(date === undefined ? null : date); }}
                                                    value={endDateTS === null ? undefined : endDateTS}
                                                    formatDate={onFormatDate}
                                                    placeholder={t('SelectEndDate')}
                                                    minDate={startDateTS === null ? undefined : startDateTS}
                                                />
                                            </Field>
                                        </div>
                                    </div>

                                </div>
                            </form>
                        </div>
                    </div>

                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestProjectAssigmentEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};